import {
  Box,
  Flex,
  Icon,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  ThemeTypings,
  Tr,
} from '@chakra-ui/react';
import { BackofficeGetGroupedPromosProductHighlightResponse } from '@diamond/shared/types';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import MenuIcon from '@mui/icons-material/Menu';
import { ReactNode, useEffect, useState } from 'react';

type ProductHighlightItem =
  BackofficeGetGroupedPromosProductHighlightResponse[number];

export interface TableColumn {
  key: string;
  header: ReactNode;
  width?: string | number;
  cell?: (item: ProductHighlightItem, index: number) => ReactNode;
  textAlign?: 'left' | 'center' | 'right';
}

interface SortableTableProps {
  items: ProductHighlightItem[];
  columns: TableColumn[];
  droppableId: string;
  onReorder?: (productItemCode: number, newIndex: number) => void;
  tableVariant?: ThemeTypings['components']['Table']['variants'];
  headerBgColor?: string;
  headerColor?: string;
  dragHandleColumn?: number;
}

export function SortableTable({
  items,
  columns,
  droppableId,
  onReorder,
  tableVariant = 'simple',
  headerBgColor = 'blue.700',
  headerColor = 'white',
  dragHandleColumn = 1,
}: SortableTableProps) {
  const [tableItems, setTableItems] = useState<ProductHighlightItem[]>(items);

  const renderTableCell = (
    column: TableColumn,
    item: ProductHighlightItem,
    index: number,
    colIndex: number
  ) => {
    if (colIndex === dragHandleColumn) {
      return (
        <Flex
          justifyContent="center"
          cursor="grab"
          _active={{ cursor: 'grabbing' }}
        >
          <Icon as={MenuIcon} color="blue.600" boxSize={6} />
        </Flex>
      );
    }
    if (column.cell) {
      return column.cell(item, index);
    }
    return (item as any)[column.key];
  };

  useEffect(() => {
    setTableItems(items);
  }, [items]);

  const handleDragEnd = (result: DropResult) => {
    if (
      !result.destination ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const currentItems = Array.from(tableItems);
    const [movedItem] = currentItems.splice(result.source.index, 1);
    currentItems.splice(result.destination.index, 0, movedItem);

    setTableItems(currentItems);

    if (onReorder) {
      const productItemCode = parseInt(result.draggableId, 10);
      const newIndex = result.destination.index;

      if (!isNaN(productItemCode)) {
        onReorder(productItemCode, newIndex);
      } else {
        console.error(
          'SortableTable: Could not parse productItemCode from draggableId:',
          result.draggableId
        );
        setTableItems(items);
      }
    }
  };

  return (
    <Box overflowX="auto" w="full">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={droppableId}>
          {(provided) => (
            <Table
              variant={tableVariant}
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              <Thead>
                <Tr>
                  {columns.map((column) => (
                    <Th
                      key={column.key}
                      width={column.width}
                      sx={{
                        '&&': {
                          bg: headerBgColor,
                          color: headerColor,
                          border: '1px solid #9999',
                        },
                      }}
                      textAlign={column.textAlign || 'left'}
                    >
                      {column.header}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {tableItems.map((item, index) => (
                  <Draggable
                    key={item.product_item_code}
                    draggableId={item.product_item_code.toString()}
                    index={index}
                  >
                    {(providedDraggable, snapshot) => (
                      <Tr
                        ref={providedDraggable.innerRef}
                        {...providedDraggable.draggableProps}
                        bg={snapshot.isDragging ? 'blue.50' : 'white'}
                        border="1px solid #9999"
                      >
                        {columns.map((column, colIndex) => (
                          <Td
                            key={`${item.product_item_code}-${column.key}`}
                            textAlign={column.textAlign || 'left'}
                            border="1px solid #9999"
                            {...(colIndex === dragHandleColumn
                              ? providedDraggable.dragHandleProps
                              : {})}
                          >
                            {renderTableCell(column, item, index, colIndex)}
                          </Td>
                        ))}
                      </Tr>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Tbody>
            </Table>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
}
